/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Signal, computed } from '@angular/core';

import { toObservable } from '@angular/core/rxjs-interop';
import { Scenario } from '@scenario/models/scenario.model';
import {
  BasecasesViewModel,
  selectBasecasesViewModel,
} from '@store/scenario/basecases/basecases-slice';
import {
  MarketAreasViewModel,
  selectMarketAreasViewModel,
} from '@store/scenario/market-areas/market-areas-slice';
import { dispatch, select } from '@store/injectables';
import { RegionsViewModel, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import {
  ExportedScenariosViewModel,
  exportSelectedScenarios,
  selectExportedScenariosViewModel,
} from '@store/scenario/scenarios/export-selected-scenarios-slice';
import {
  ScenariosViewModel,
  selectScenariosViewModel,
} from '@store/scenario/scenarios/scenarios-slice';
import { selectSelectedScenarios } from '@store/scenario/scenarios/selected-scenarios-slice';
import { SourcesViewModel, selectSourcesViewModel } from '@store/scenario/sources/sources-slice';
import {
  SubtypesViewModel,
  selectSubtypesViewModel,
} from '@store/scenario/subtypes/subtypes-slice';
import { UnitRate } from '@scenario/models/unit-rate.model';
import {
  selectEurNominalUnitRate,
  selectSelectedNominalUnitRate,
  selectSelectedUnitRates,
} from '@store/scenario/unit-rates/selected-unit-rates-slice';

import {
  WeatherYearsViewModel,
  selectWeatherYearsViewModel,
} from '@store/scenario/weather-years/weather-years-slice';
import { InsightService } from '@app/services/insight.service';
import {
  UnitRatesViewModel,
  selectUnitRatesViewModel,
} from '@store/scenario/unit-rates/unit-rates-slice';
import {
  UserPreferencesViewModel,
  saveUserPreferences,
  selectUserPreferencesViewModel,
} from '@store/scenario/user-preferences/user-preferences-slice';
import { UserPreferences } from '@scenario/models/user-preferences.model';
import { SearchScenariosFormContainerComponent } from '../search-scenarios-form-container/search-scenarios-form-container.component';
import {
  ButtonComponent,
 FormFieldComponent, IconComponent,
  SegmentedControlComponent,
  SegmentedControlOptionComponent,
  TileComponent,
} from '@totalenergiescode/tds-angular';
import { ScenarioUnitFormComponent } from '../scenario-unit-form/scenario-unit-form.component';
import { AsyncPipe } from '@angular/common';
import { ScenariosResultListContainerComponent } from '../scenarios-result-list-container/scenarios-result-list-container.component';
import { PricesChartComponent } from '../chart/prices-chart/prices-chart.component';
import { ScenariosPriceListComponent } from '../scenarios-price-list/scenarios-price-list.component';
import { HistoricalDataMode } from '@scenario/enums/historical-data-mode.enum';
import { saveHistoricalDataMode, selectHistoricalDataMode } from '@store/scenario/app/app-slice';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InfoIconTooltipComponent } from '@app/tooltips/info-icon-tooltip/info-icon-tooltip.component';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-scenarios-finder-page',
  templateUrl: './scenarios-finder-page.component.html',
  styleUrl: './scenarios-finder-page.component.scss',
  imports: [SearchScenariosFormContainerComponent, IconComponent, ScenarioUnitFormComponent, ButtonComponent, TileComponent, ScenariosResultListContainerComponent, SegmentedControlComponent, SegmentedControlOptionComponent, PricesChartComponent, ScenariosPriceListComponent, AsyncPipe, FormFieldComponent, FormsModule, InfoIconTooltipComponent, MatFormField, MatLabel, MatSelect, MatOption, ReactiveFormsModule, MatTooltip]
})
export class ScenariosFinderPageComponent {
  dispatch = dispatch();
  scenariosViewModelSignal: Signal<ScenariosViewModel> = select(selectScenariosViewModel);
  scenariosViewModel$ = toObservable(this.scenariosViewModelSignal);
  regionsViewModelSignal: Signal<RegionsViewModel> = select(selectRegionsViewModel);
  regionsViewModel$ = toObservable(this.regionsViewModelSignal);
  sourcesViewModelSignal: Signal<SourcesViewModel> = select(selectSourcesViewModel);
  sourcesViewModel$ = toObservable(this.sourcesViewModelSignal);
  basecasesViewModelSignal: Signal<BasecasesViewModel> = select(selectBasecasesViewModel);
  basecasesViewModel$ = toObservable(this.basecasesViewModelSignal);
  subtypesViewModelSignal: Signal<SubtypesViewModel> = select(selectSubtypesViewModel);
  subtypesViewModel$ = toObservable(this.subtypesViewModelSignal);
  selectedScenariosSignal: Signal<Scenario[]> = select(selectSelectedScenarios);
  selectedUnitRatesSignal: Signal<UnitRate> = select(selectSelectedUnitRates);
  selectedNominalUnitRateSignal: Signal<UnitRate> = select(selectSelectedNominalUnitRate);
  selectedEurNominalUnitRateSignal: Signal<UnitRate> = select(selectEurNominalUnitRate);
  unitRatesSignal: Signal<UnitRatesViewModel> = select(selectUnitRatesViewModel);
  exportSelectedScenariosSignal: Signal<ExportedScenariosViewModel> = select(
    selectExportedScenariosViewModel,
  );
  weatherYearsViewModelSignal: Signal<WeatherYearsViewModel> = select(selectWeatherYearsViewModel);
  weatherYearsViewModel$ = toObservable(this.weatherYearsViewModelSignal);
  marketAreasViewModelSignal: Signal<MarketAreasViewModel> = select(selectMarketAreasViewModel);
  marketAreasViewModel$ = toObservable(this.marketAreasViewModelSignal);
  userPreferences: Signal<UserPreferencesViewModel> = select(selectUserPreferencesViewModel);
  display = computed(() => this.userPreferences().userPreferences.display);
  displaySelected = computed(() => {
    switch (this.display()) {
      case 'TABLE':
        return 0;
      case 'CHART':
        return 1;
      default:
        return 0;
    }
  });
  historicalDataMode: Signal<HistoricalDataMode> = select(selectHistoricalDataMode);
  historicalDataModes = Object.values(HistoricalDataMode);
  previewTitle = computed(() => {
    const unit = this.selectedUnitRatesSignal().to;
    const unitDescription =
      unit.term === 'NOMINAL'
        ? `${unit.currency}, ${unit.term}`
        : `${unit.currency}, ${unit.realYear} ${unit.term}`;

    switch (this.historicalDataMode()) {
      case HistoricalDataMode.FORECAST:
        return `Preview - Forecast (${unitDescription})`;
      case HistoricalDataMode.COMBINED:
        return `Preview - Historical (${unit.currency}, NOMINAL) / Forecast (${unitDescription})`;
      case HistoricalDataMode.HISTORICAL:
        return `Preview - Historical (${unitDescription})`;
      default:
        return `Preview - Forecast (${unitDescription})`;
    }
  });

  constructor(private readonly insightService: InsightService) {}

  onHistoricalDataModeChange(mode: string): void {
    this.insightService.addPreviewAttributesAwsRum(null, mode);
    this.insightService.recordEvent('update_preview_type', {});
    this.dispatch(saveHistoricalDataMode(mode as HistoricalDataMode));
  }

  exportSelectedScenarios() {
    const ratesByYear = this.selectedUnitRatesSignal().ratesByYear;
    const eurNominalUnitRate = this.selectedEurNominalUnitRateSignal();
    const eurNominalRatesByYear = eurNominalUnitRate.ratesByYear;
    const unitRateTo = this.selectedUnitRatesSignal().to;
    const unitRateFrom = this.selectedUnitRatesSignal().from;
    const scenarios = this.selectedScenariosSignal();
    const withUnitRateScenarios = scenarios.map(scenario => ({
      ...scenario,
      pricePerYear: this.applyRateByYear(scenario.pricePerYear, ratesByYear),
      historicalPricePerYear: this.applyRateByYear(scenario.historicalPricePerYear, ratesByYear),
      unitRateCurrency: unitRateTo.currency,
      unitRateTermYear: unitRateTo.term + ' ' + (unitRateTo.realYear || ''),
    }));
    const rawScenarios = scenarios.map(scenario => ({
      ...scenario,
      unitRateCurrency: unitRateFrom.currency,
      unitRateTermYear: unitRateFrom.term + ' ' + (unitRateFrom.realYear || ''),
      historicalPricePerYear: this.applyRateByYear(scenario.historicalPricePerYear, eurNominalRatesByYear),
      historicalUnitRateCurrency: eurNominalUnitRate.to.currency,
      historicalUnitRateTermYear: eurNominalUnitRate.to.term + ' ' + (eurNominalUnitRate.to.realYear || ''),
    }));
    const selectedScenariosCount = withUnitRateScenarios.length;
    this.insightService.recordEvent('export', { selectedScenariosCount: selectedScenariosCount });
    this.dispatch(
      exportSelectedScenarios({
        scenarios: withUnitRateScenarios,
        rawScenarios: rawScenarios,
        unitRates: this.unitRatesSignal().unitRates,
      }),
    );
  }

  applyRateByYear = (
    pricesByYear: Record<string, number | null>,
    ratesByYear: Record<string, number>,
  ): Record<string, number | null> => {
    return Object.fromEntries(
      Object.entries(pricesByYear).map(([year, price]) => {
        if (price !== null) {
          return [year, price * (ratesByYear[year] || 1)];
        }
        return [year, null];
      }),
    );
  };

  switchView(selectedIndex: number) {
    let display;
    switch (selectedIndex) {
      case 0:
        display = 'TABLE';
        break;
      case 1:
        display = 'CHART';
        break;
      default:
        display = 'TABLE';
    }
    this.saveUserPreferencesDisplay(display);
    this.insightService.addPreviewAttributesAwsRum(display, null);
    this.insightService.recordEvent('update_preview_style', {});
  }


  saveUserPreferencesDisplay(display: string): void {
    const userPreferences: UserPreferences = {
      unitId: this.userPreferences().userPreferences.unitId,
      unitCurrency: this.userPreferences().userPreferences.unitCurrency,
      unitTerm: this.userPreferences().userPreferences.unitTerm,
      unitRealYear: this.userPreferences().userPreferences.unitRealYear,
      advanced: this.userPreferences().userPreferences.advanced,
      display: display,
    };
    this.dispatch(saveUserPreferences(userPreferences));
  }
}
